<script>
  // A single list row in the CardCollection shell (ADR-0010). Renders the
  // normalized card-item model; a tap expands who has it.
  import { getCardImageUrl } from '../../lib/services/card-utils.js';
  import { deriveOwners } from '../../lib/logic/card-owners.js';
  import CardHolders from './CardHolders.svelte';
  import OwnerAvatars from './OwnerAvatars.svelte';
  import CardPrice from './CardPrice.svelte';

  let { item, expanded = false, currencySymbol = '$', onToggle, onCardClick = null } = $props();

  const imageUrl = $derived(getCardImageUrl(item.imageRef, 'small'));
  const setLabel = $derived((item.set || '').toUpperCase());
  const rarityClass = $derived(`rarity-${(item.rarity || 'common').toLowerCase()}`);
  const owners = $derived(deriveOwners(item.holders));

  function handleKeydown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle?.();
    }
  }

  function thumbClick(e) {
    e.stopPropagation();
    if (onCardClick) onCardClick(item);
  }
</script>

<div class="cc-row-wrapper" class:expanded>
  <div class="cc-row" role="button" tabindex="0" onclick={onToggle} onkeydown={handleKeydown}>
    <!-- Name column -->
    <div class="row-name-col">
      <button
        class="row-thumb-btn"
        class:no-action={!onCardClick}
        onclick={thumbClick}
        aria-label="View {item.name} details"
        disabled={!onCardClick}
      >
        {#if imageUrl}
          <img
            class="row-thumb"
            src={imageUrl}
            alt={item.name}
            loading="lazy"
            width="20"
            height="28"
          />
        {:else}
          <div class="row-thumb-placeholder"></div>
        {/if}
        {#if owners.length > 1}
          <div class="row-thumb-count">{owners.length}</div>
        {/if}
      </button>
      <span class="row-name">{item.name}</span>
      {#if item.finish === 'foil'}
        <span class="row-foil-badge">FOIL</span>
      {:else if item.finish === 'etched'}
        <span class="row-foil-badge etched">ETCHED</span>
      {/if}
    </div>

    <!-- Type column -->
    <span class="row-type">{item.typeLine || ''}</span>

    <!-- Set column -->
    <span class="row-set">{setLabel}</span>

    <!-- Rarity column -->
    <span class="row-rarity-dot {rarityClass}"></span>

    <!-- Owners column -->
    <div class="row-owners">
      <OwnerAvatars {owners} max={4} size={20} overlap={4} />
    </div>

    <!-- Price column (Sample Card → "from" floor) -->
    <span class="row-price">
      <CardPrice price={item.price} {currencySymbol} isSample={item.isSample} />
    </span>
  </div>

  <!-- Expanded who-has-it panel -->
  {#if expanded}
    <div class="cc-row-detail">
      {#if item.holders && item.holders.length}
        <CardHolders holders={item.holders} {currencySymbol} />
      {:else}
        <p class="no-holders">No one you share a server with has this.</p>
      {/if}
    </div>
  {/if}
</div>

<style>
  .cc-row-wrapper {
    border-bottom: 1px solid var(--color-border);
    transition: background 0.1s;
  }
  .cc-row-wrapper:last-child {
    border-bottom: none;
  }

  .cc-row {
    display: grid;
    /* Consumes --cc-grid-cols defined once on .cc-list (review §4); falls back to
       the literal track for safety if a row is ever rendered outside .cc-list. */
    grid-template-columns: var(
      --cc-grid-cols,
      minmax(200px, 2fr) 110px 80px 50px minmax(80px, 1fr) 100px
    );
    align-items: center;
    gap: 0.5rem;
    padding: 12px 20px;
    cursor: pointer;
    transition: background 0.1s;
    user-select: none;
  }
  .cc-row:hover {
    background: rgba(0, 0, 0, 0.015);
  }

  .row-name-col {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }
  .row-thumb-btn {
    position: relative;
    flex-shrink: 0;
    width: 20px;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    border-radius: 3px;
  }
  .row-thumb-btn.no-action {
    cursor: default;
  }
  .row-thumb {
    width: 20px;
    height: 28px;
    border-radius: 3px;
    object-fit: cover;
    display: block;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  }
  .row-thumb-placeholder {
    width: 20px;
    height: 28px;
    background: var(--color-accent-bg);
    border-radius: 3px;
  }
  .row-thumb-count {
    position: absolute;
    top: -4px;
    right: -5px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: var(--color-accent-dark);
    color: var(--color-bg);
    font-size: 8px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1.5px solid var(--color-bg);
  }
  .row-name {
    font-family: var(--font-serif);
    font-size: 14px;
    font-weight: 600;
    color: var(--color-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .row-foil-badge {
    font-size: 8px;
    font-weight: 600;
    color: var(--color-text-muted);
    border: 1px solid var(--color-border);
    border-radius: 3px;
    padding: 1px 5px;
    font-family: var(--font-mono);
    flex-shrink: 0;
    letter-spacing: 0.5px;
  }

  /* The type / set / rarity / owners columns exist only when the list's track
     has room for them: .cc-list sets --cc-meta-display: none when it drops
     them (phones). One owner, no duplicated breakpoint here. */
  .row-type,
  .row-set,
  .row-rarity-dot {
    display: var(--cc-meta-display, block);
  }
  .row-owners {
    display: var(--cc-meta-display, flex);
    align-items: center;
  }

  .row-type {
    color: var(--color-text-tertiary);
    font-family: var(--font-mono);
    font-size: 12px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .row-set {
    color: var(--color-text-tertiary);
    font-family: var(--font-mono);
    font-size: 12px;
  }

  .row-rarity-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--color-rarity-common);
  }
  .row-rarity-dot.rarity-mythic {
    background: var(--color-rarity-mythic);
  }
  .row-rarity-dot.rarity-rare {
    background: var(--color-rarity-rare);
  }
  .row-rarity-dot.rarity-uncommon {
    background: var(--color-rarity-uncommon);
  }

  .row-price {
    text-align: right;
  }

  .cc-row-detail {
    background: var(--color-bg);
    border-top: 1px solid var(--color-border);
    padding: 0.625rem 20px 0.625rem 50px;
  }
  .no-holders {
    margin: 0;
    color: var(--color-text-tertiary);
    font-size: 0.8125rem;
  }

  @media (max-width: 768px) {
    /* Name + price. The meta columns are already gone (.cc-list sets
       --cc-meta-display: none at this width). */
    .cc-row {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      grid-template-areas: 'name price';
      gap: 0.5rem;
      padding: 10px 12px;
    }
    .row-name-col {
      grid-area: name;
    }
    .row-name {
      font-size: 13px;
    }
    .row-price {
      grid-area: price;
      white-space: nowrap;
      font-size: 13px;
    }
    .cc-row-detail {
      padding-left: 12px;
    }
  }
</style>
